<template>
  <PrimevueDialog
    v-model:visible="dialogVisible"
    :modal="true"
    :style="{ width: '620px', maxWidth: '95vw' }"
    header="Imprimer une étiquette"
  >
    <div v-if="!barcodeValue" class="alert alert-warning py-2 mb-3">
      Aucun code-barres n’est disponible pour ce produit.
    </div>

    <div class="product-label-dialog-layout">
      <div class="product-label-preview-panel">
        <div class="product-label-preview">
          <div class="product-label-name">{{ labelName }}</div>
          <div class="product-label-price">{{ formattedPrice }}</div>
          <div class="product-label-barcode" v-html="barcodeSvg"></div>
          <div class="product-label-number">{{ barcodeValue || '—' }}</div>
        </div>
      </div>

      <div class="product-label-options">
        <div class="fw-semibold mb-2">Quantité à imprimer</div>

        <label class="form-check product-label-option">
          <input
            v-model="labelMode"
            class="form-check-input"
            type="radio"
            :name="radioName"
            value="single"
          />
          <span class="form-check-label">Une seule étiquette</span>
        </label>

        <label v-if="bulkChoiceVisible" class="form-check product-label-option">
          <input
            v-model="labelMode"
            class="form-check-input"
            type="radio"
            :name="radioName"
            value="quantity"
          />
          <span class="form-check-label">Quantité commandée ({{ bulkQuantity }})</span>
        </label>
      </div>
    </div>

    <div class="suite241-product-label-print-area" aria-hidden="true">
      <div v-for="copy in printCopiesList" :key="copy" class="suite241-product-label-print-copy">
        <div class="product-label-name">{{ labelName }}</div>
        <div class="product-label-price">{{ formattedPrice }}</div>
        <div class="product-label-barcode" v-html="barcodeSvg"></div>
        <div class="product-label-number">{{ barcodeValue }}</div>
        <div class="product-label-powered-by">powered by Suite241</div>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeDialog">
          <i class="fa fa-xmark me-1" aria-hidden="true"></i>
          Fermer
      </button>
      <button type="button" class="btn btn-primary btn-sm btn-icon-label" :disabled="!canPrint" @click="printLabels">
        <i class="fa fa-print"></i>
        <span class="btn-label ms-1">Imprimer</span>
      </button>
    </template>
  </PrimevueDialog>
</template>

<script>
import { code128Svg, normalizeCode128Value } from '@/support/code128';

export default {
  name: 'ProductLabelPrintDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    productName: {
      type: String,
      default: '',
    },
    salePrice: {
      type: [Number, String],
      default: 0,
    },
    barcode: {
      type: String,
      default: '',
    },
    quantity: {
      type: [Number, String],
      default: 1,
    },
    showQuantityChoice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible'],
  data() {
    return {
      labelMode: 'single',
      radioName: `product-label-mode-${Math.random().toString(36).slice(2)}`,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
    labelName() {
      return this.productName || 'Produit';
    },
    barcodeValue() {
      return normalizeCode128Value(this.barcode);
    },
    barcodeSvg() {
      return code128Svg(this.barcodeValue);
    },
    numericPrice() {
      const price = Number(this.salePrice || 0);

      return Number.isFinite(price) ? price : 0;
    },
    formattedPrice() {
      if (this.$format?.currency) {
        return this.$format.currency(this.numericPrice);
      }

      return `${this.numericPrice.toLocaleString('fr-FR')} FCFA`;
    },
    bulkQuantity() {
      const quantity = Math.floor(Number(this.quantity || 1));

      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    },
    bulkChoiceVisible() {
      return this.showQuantityChoice && this.bulkQuantity > 1;
    },
    printCopies() {
      return this.labelMode === 'quantity' && this.bulkChoiceVisible ? this.bulkQuantity : 1;
    },
    printCopiesList() {
      return Array.from({ length: this.printCopies }, (_, index) => index + 1);
    },
    canPrint() {
      return Boolean(this.barcodeValue);
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.labelMode = 'single';
      }
    },
  },
  beforeUnmount() {
    this.cleanupPrintMode();
  },
  methods: {
    closeDialog() {
      this.dialogVisible = false;
    },
    printLabels() {
      if (!this.canPrint) return;

      document.body.classList.add('suite241-printing-product-label');

      this.$nextTick(() => {
        const cleanup = () => this.cleanupPrintMode();
        window.addEventListener('afterprint', cleanup, { once: true });
        window.print();
        window.setTimeout(cleanup, 500);
      });
    },
    cleanupPrintMode() {
      document.body.classList.remove('suite241-printing-product-label');
    },
  },
};
</script>

<style scoped>
.product-label-dialog-layout {
  align-items: stretch;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 0.72fr);
}

.product-label-preview-panel {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e5ebf4;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  min-height: 210px;
  padding: 1rem;
}

.product-label-preview,
.suite241-product-label-print-copy {
  background: #ffffff;
  border: 1px solid #111827;
  border-radius: 4px;
  color: #111827;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  justify-content: center;
  min-height: 148px;
  padding: 0.65rem;
  text-align: center;
  width: 250px;
}

.product-label-name {
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.product-label-price {
  font-size: 1.15rem;
  font-weight: 900;
}

.product-label-barcode {
  margin: 0.15rem auto 0;
  max-width: 100%;
  width: 100%;
}

.product-label-barcode :deep(svg) {
  display: block;
  height: 54px;
  width: 100%;
}

.product-label-number {
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
}

.product-label-options {
  background: #ffffff;
  border: 1px solid #e5ebf4;
  border-radius: 8px;
  padding: 0.85rem;
}

.product-label-option {
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.product-label-option:last-child {
  margin-bottom: 0;
}

.suite241-product-label-print-area {
  display: none;
}

.product-label-powered-by {
  font-size: 7px;
  margin-top: 2px;
  text-align: center;
}

@media (max-width: 640px) {
  .product-label-dialog-layout {
    grid-template-columns: 1fr;
  }
}

@media print {
  :global(body.suite241-printing-product-label *) {
    visibility: hidden;
  }

  :global(body.suite241-printing-product-label .suite241-product-label-print-area),
  :global(body.suite241-printing-product-label .suite241-product-label-print-area *) {
    visibility: visible;
  }

  :global(body.suite241-printing-product-label .suite241-product-label-print-area) {
    align-items: start;
    display: grid;
    gap: 4mm;
    grid-template-columns: repeat(auto-fill, minmax(60mm, 1fr));
    left: 0;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  :global(body.suite241-printing-product-label .suite241-product-label-print-copy) {
    break-inside: avoid;
    min-height: 35mm;
    page-break-inside: avoid;
    width: 60mm;
  }

  :global(body.suite241-printing-product-label .product-label-barcode svg) {
    height: 16mm;
  }

  @page {
    margin: 8mm;
  }
}
</style>
